import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { Field, FieldValues, Path, UseFormReturn, useWatch } from "react-hook-form";

import { getCategories } from "../../core/category.core";
import localeCore from "../../core/locale.core";
import { useTranslation } from "../../core/translation.core";
import ChevronDownIcon from "../../icons/chevronDown.icon";
import UpIcon from "../../icons/up.icon";
import { Category } from "../../types/category";
import { FancyCheckbox } from "../reuseables/form.comp";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";

export default function CategorySelectorComp<T extends FieldValues>({
  methods,
  name,
  labelMaxLength = 30,
}: {
  methods: UseFormReturn<T, any>;
  name: Path<T>;
  labelMaxLength?: number;
}) {
  const [isOpened, setIsOpened] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  function onClick(e: MouseEvent) {
    // This is a hack to get the latest state
    if (!ref || !ref.current) return;
    const isOpened = (ref.current as any).getAttribute("data-state");

    if (!isOpened) return;

    const isWithin = (e.target as any).closest("#select-category");

    if (!isWithin) {
      setIsOpened(false);
    }
  }

  const css =
    "relative " +
    "h-9 w-full " +
    "dark:bg-her-backblue " +
    `border rounded-t-md ${
      !isOpened ? "rounded-b-md" : ""
    } hover:border-blue-200 hover:border-2 focus-visible:border-blue-600 focus-visible:border-2 ` +
    "focus-visible:outline-none outline-none " +
    "focus-visible:shadow-sm focus-visible:shadow-blue-800 " +
    "duration-1000 transition-[box-shadow] " +
    "disabled:text-her-lightgray2 text-base ";

  return (
    <div id="select-category" ref={ref} data-state={isOpened} className={css}>
      <button
        type="button"
        className="w-full h-full flex justify-between pr-2 pl-4 items-center"
        onClick={() => setIsOpened((value) => !value)}
      >
        <CurrentName methods={methods} name={name} labelMaxLength={labelMaxLength} /> <ChevronDownIcon />
      </button>
      {isOpened && <Panel methods={methods} name={name} />}
    </div>
  );
}

function CurrentName<T extends FieldValues>({
  methods,
  name,
  labelMaxLength,
}: {
  methods: UseFormReturn<T, any>;
  name: Path<T>;
  labelMaxLength: number;
}) {
  const catIds = useWatch({ name, control: methods.control }) as string[];
  const { msg, t } = useTranslation();
  const { data: categories, isFetched } = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
  });
  const { locale, isReady } = useRouter();

  if (!isFetched) return null;
  if (!categories) return null;
  if (!isReady) return null;

  if (!catIds || catIds.length === 0) return <div>{t(msg.category.selectCategories)}</div>;

  const names = catIds.map((catId) => {
    const foundCat = categories.find((c) => c.id === catId);
    if (!foundCat) return undefined;
    return localeCore.getLocaleValueFallback(foundCat.name, locale!);
  });

  let text = names.join(", ");

  if (text.length > labelMaxLength) {
    text = text.substring(0, labelMaxLength - 3) + " ...";
  }

  return <div>{text}</div>;
}

function Panel<T extends FieldValues>({ methods, name }: { methods: UseFormReturn<T, any>; name: Path<T> }) {
  const {
    data: categories,
    isError,
    isPending,
    error,
  } = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
  });

  if (isError) return <ErrorCenterIcon reason={error.message} />;
  if (isPending) return <LoadingOvalCenterIcon />;
  if (!categories) return null;

  const topCats = categories.filter((c) => !c.parentId);

  return (
    <div className="category_panel absolute top-[34px] left-0 w-full border rounded-b-md hover:border-blue-200 bg-white dark:bg-her-backblue space-y-1 px-4 py-4 z-10 max-h-72 overflow-y-auto">
      {topCats.map((c) => (
        <CatRow key={c.id} cat={c} categories={categories} level={0} methods={methods} name={name} />
      ))}
    </div>
  );
}

function CatRow<T extends FieldValues>({
  cat,
  categories,
  level,
  methods,
  name,
}: {
  cat: Category;
  categories: Category[];
  level: number;
  methods: UseFormReturn<T, any>;
  name: Path<T>;
}) {
  const children = categories.filter((c) => c.parentId === cat.id);

  return (
    <div data-level={level} data-code={cat.code} className="w-full text-left">
      <CatButton level={level} cat={cat} methods={methods} name={name} />
      {children.length > 0 && (
        <div className="space-y-1 mt-2">
          {children.map((childCat) => (
            <CatRow key={childCat.id} cat={childCat} categories={categories} level={level + 1} methods={methods} name={name} />
          ))}
        </div>
      )}
    </div>
  );
}

function CatButton<T extends FieldValues>({
  level,
  cat,
  methods,
  name,
}: {
  level: number;
  cat: Category;
  methods: UseFormReturn<T, any>;
  name: Path<T>;
}) {
  const { locale, isReady } = useRouter();

  if (!isReady) return null;

  return (
    <FancyCheckbox value={cat.id} register={methods.register(name)}>
      <Indent level={level} />
      {localeCore.getLocaleValueFallback(cat.name, locale!)}
    </FancyCheckbox>
  );
}

function Indent({ level }: { level: number }) {
  let elements: JSX.Element[] = [];

  for (let i = 0; i < level; i++) {
    elements.push(<UpIcon key={level} className="w-4 h-4" />);
  }
  return <>{elements}</>;
}
